import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, MapPin, Package, DollarSign, Calendar, Phone, Mail, User, Edit, Save, RotateCcw, Plus, Trash2, X } from 'lucide-react';
import { backendService } from '../lib/api';

const QuoteDetailsPage = () => {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const [quoteDetails, setQuoteDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [statusUpdate, setStatusUpdate] = useState({
    status: 'pending',
    admin_notes: ''
  });
  const [editData, setEditData] = useState({
    customer_name: '',
    customer_email: '',
    customer_phone: '',
    monthly_rental: 0,
    laundering_per_visit: 0,
    delivery_charges_per_visit: 0
  });
  const [editProperties, setEditProperties] = useState([]);
  const [availableItems, setAvailableItems] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (quoteId) {
      fetchQuoteDetails();
      fetchAvailableItems();
    }
  }, [quoteId]);

  useEffect(() => {
    if (quoteDetails) {
      setEditData({
        customer_name: quoteDetails.customer_name || '',
        customer_email: quoteDetails.customer_email || '',
        customer_phone: quoteDetails.customer_phone || '',
        monthly_rental: quoteDetails.monthly_rental || 0,
        laundering_per_visit: quoteDetails.laundering_per_visit || 0,
        delivery_charges_per_visit: quoteDetails.delivery_charges_per_visit || 0
      });
      setEditProperties(quoteDetails.quote_properties || []);
      setStatusUpdate({
        status: quoteDetails.status || 'pending',
        admin_notes: quoteDetails.admin_notes || ''
      });
    }
  }, [quoteDetails]);

  const fetchQuoteDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await backendService.request(`/quotes/${quoteId}`, { method: 'GET' });
      setQuoteDetails(response.quote);
    } catch (err) {
      setError('Failed to load quote details');
      console.error('Error fetching quote details:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableItems = async () => {
    try {
      const response = await backendService.request('/items', { method: 'GET' });
      setAvailableItems(response.items || []);
    } catch (err) {
      console.error('Error fetching available items:', err);
    }
  };

  const handleSaveEdit = async () => {
    setIsSaving(true);
    try {
      // Get CSRF token first
      await backendService.getCsrfToken();

      // Save quote details
      await backendService.request(`/quotes/${quoteId}`, {
        method: 'PUT',
        body: JSON.stringify(editData)
      });

      // Save property items
      for (const property of editProperties) {
        if (property.quote_items && property.quote_items.length > 0) {
          await backendService.request(`/quote-properties/${property.id}/items`, {
            method: 'PUT',
            body: JSON.stringify({ items: property.quote_items })
          });
        }
      }

      // Refresh quote details
      await fetchQuoteDetails();
      setIsEditing(false);
      alert('Quote details updated successfully!');
    } catch (err) {
      alert('Failed to update quote details');
      console.error('Error updating quote details:', err);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    if (quoteDetails) {
      setEditData({
        customer_name: quoteDetails.customer_name || '',
        customer_email: quoteDetails.customer_email || '',
        customer_phone: quoteDetails.customer_phone || '',
        monthly_rental: quoteDetails.monthly_rental || 0,
        laundering_per_visit: quoteDetails.laundering_per_visit || 0,
        delivery_charges_per_visit: quoteDetails.delivery_charges_per_visit || 0
      });
      setEditProperties(quoteDetails.quote_properties || []);
    }
  };

  const handleStatusUpdate = async () => {
    try {
      // Get CSRF token first
      await backendService.getCsrfToken();

      await backendService.request(`/quotes/${quoteId}/status`, {
        method: 'PUT',
        body: JSON.stringify(statusUpdate)
      });
      
      // Update local state
      setQuoteDetails(prev => ({
        ...prev,
        status: statusUpdate.status,
        admin_notes: statusUpdate.admin_notes
      }));
      
      alert('Quote status updated successfully!');
    } catch (err) {
      alert('Failed to update quote status');
      console.error('Error updating quote status:', err);
    }
  };

  const addItemToProperty = (propertyId) => {
    const newItem = {
      id: `temp-${Date.now()}`,
      item_id: '',
      quantity: 1,
      unit_rental_price: 0,
      total_rental_price: 0,
      unit_laundering_price: 0,
      total_laundering_price: 0,
      isNew: true
    };

    setEditProperties(prev => prev.map(property => {
      if (property.id === propertyId) {
        return {
          ...property,
          quote_items: [...(property.quote_items || []), newItem]
        };
      }
      return property;
    }));
  };

  const removeItemFromProperty = (propertyId, itemId) => {
    setEditProperties(prev => prev.map(property => {
      if (property.id === propertyId) {
        return {
          ...property,
          quote_items: property.quote_items?.filter(item => item.id !== itemId) || []
        };
      }
      return property;
    }));
  };

  const updatePropertyItem = (propertyId, itemId, field, value) => {
    setEditProperties(prev => prev.map(property => {
      if (property.id === propertyId) {
        return {
          ...property,
          quote_items: property.quote_items?.map(item => {
            if (item.id === itemId) {
              const updatedItem = { ...item, [field]: value };
              
              // Recalculate totals if quantity or unit prices change
              if (field === 'quantity' || field === 'unit_rental_price') {
                updatedItem.total_rental_price = (updatedItem.quantity || 0) * (updatedItem.unit_rental_price || 0);
              }
              if (field === 'quantity' || field === 'unit_laundering_price') {
                updatedItem.total_laundering_price = (updatedItem.quantity || 0) * (updatedItem.unit_laundering_price || 0);
              }
              
              return updatedItem;
            }
            return item;
          }) || []
        };
      }
      return property;
    }));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'reviewed': return 'bg-blue-100 text-blue-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'Pending';
      case 'reviewed': return 'Reviewed';
      case 'approved': return 'Approved';
      case 'rejected': return 'Rejected';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading quote details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Package className="w-6 h-6 text-red-600" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Error</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/admin')}
            className="btn-secondary"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/admin')}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Quote Details</h1>
                <p className="text-sm text-gray-600">Quote #{quoteDetails?.id}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {!isEditing ? (
                <button
                  onClick={() => setIsEditing(true)}
                  className="btn-secondary flex items-center gap-2"
                >
                  <Edit className="w-4 h-4" />
                  Edit Quote
                </button>
              ) : (
                <>
                  <button
                    onClick={handleSaveEdit}
                    disabled={isSaving}
                    className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSaving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        Please wait...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4" />
                        Save Changes
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleCancelEdit}
                    className="btn-secondary flex items-center gap-2"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Current Status Display */}
        <div className="bg-white rounded-lg p-6 mb-8 border border-gray-200">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-4">
              <span className="text-sm text-gray-600">Current Status:</span>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(quoteDetails?.status)}`}>
                {getStatusLabel(quoteDetails?.status)}
              </span>
              {quoteDetails?.admin_notes && (
                <div className="text-sm text-gray-600">
                  <span className="font-medium">Notes:</span> {quoteDetails.admin_notes}
                </div>
              )}
            </div>
            <button
              onClick={() => setIsStatusModalOpen(true)}
              className="btn-secondary flex items-center gap-2"
            >
              <Edit className="w-4 h-4" />
              Update Status
            </button>
          </div>
        </div>

        {/* Full Width Content */}
        <div className="space-y-6">
          {/* Customer Information */}
          <div className="bg-white rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
              <User className="w-5 h-5" />
              Customer Information
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center gap-3">
                <User className="w-4 h-4 text-gray-500" />
                <div className="flex-1">
                  <p className="text-sm text-gray-600">Name</p>
                  {isEditing ? (
                    <input
                      type="text"
                      value={editData.customer_name}
                      onChange={(e) => setEditData(prev => ({ ...prev, customer_name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    />
                  ) : (
                    <p className="font-medium text-gray-900">{quoteDetails?.customer_name}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-gray-500" />
                <div className="flex-1">
                  <p className="text-sm text-gray-600">Email</p>
                  {isEditing ? (
                    <input
                      type="email"
                      value={editData.customer_email}
                      onChange={(e) => setEditData(prev => ({ ...prev, customer_email: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    />
                  ) : (
                    <p className="font-medium text-gray-900">{quoteDetails?.customer_email}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-gray-500" />
                <div className="flex-1">
                  <p className="text-sm text-gray-600">Phone</p>
                  {isEditing ? (
                    <input
                      type="tel"
                      value={editData.customer_phone}
                      onChange={(e) => setEditData(prev => ({ ...prev, customer_phone: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                    />
                  ) : (
                    <p className="font-medium text-gray-900">{quoteDetails?.customer_phone}</p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Calendar className="w-4 h-4 text-gray-500" />
                <div>
                  <p className="text-sm text-gray-600">Submitted</p>
                  <p className="font-medium text-gray-900">
                    {new Date(quoteDetails?.created_at).toLocaleDateString()} at{' '}
                    {new Date(quoteDetails?.created_at).toLocaleTimeString()}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Quote Summary */}
          <div className="bg-blue-50 rounded-lg p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Quote Summary
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg p-4">
                <p className="text-sm text-gray-600">Monthly Rental</p>
                {isEditing ? (
                  <input
                    type="number"
                    step="0.01"
                    value={editData.monthly_rental}
                    onChange={(e) => setEditData(prev => ({ ...prev, monthly_rental: parseFloat(e.target.value) || 0 }))}
                    className="w-full text-xl font-semibold text-gray-900 bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-red-500 rounded px-2 py-1"
                  />
                ) : (
                  <p className="text-xl font-semibold text-gray-900">${quoteDetails?.monthly_rental}</p>
                )}
              </div>
              <div className="bg-white rounded-lg p-4">
                <p className="text-sm text-gray-600">Laundering Per Visit</p>
                {isEditing ? (
                  <input
                    type="number"
                    step="0.01"
                    value={editData.laundering_per_visit}
                    onChange={(e) => setEditData(prev => ({ ...prev, laundering_per_visit: parseFloat(e.target.value) || 0 }))}
                    className="w-full text-xl font-semibold text-gray-900 bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-red-500 rounded px-2 py-1"
                  />
                ) : (
                  <p className="text-xl font-semibold text-gray-900">${quoteDetails?.laundering_per_visit}</p>
                )}
              </div>
              <div className="bg-white rounded-lg p-4">
                <p className="text-sm text-gray-600">Delivery Charges</p>
                {isEditing ? (
                  <input
                    type="number"
                    step="0.01"
                    value={editData.delivery_charges_per_visit}
                    onChange={(e) => setEditData(prev => ({ ...prev, delivery_charges_per_visit: parseFloat(e.target.value) || 0 }))}
                    className="w-full text-xl font-semibold text-gray-900 bg-transparent border-none focus:outline-none focus:ring-2 focus:ring-red-500 rounded px-2 py-1"
                  />
                ) : (
                  <p className="text-xl font-semibold text-gray-900">${quoteDetails?.delivery_charges_per_visit}</p>
                )}
              </div>
              <div className="bg-white rounded-lg p-4">
                <p className="text-sm text-gray-600">Total Per Visit</p>
                <p className="text-xl font-semibold text-gray-900">
                  ${isEditing 
                    ? (parseFloat(editData.laundering_per_visit || 0) + parseFloat(editData.delivery_charges_per_visit || 0)).toFixed(2)
                    : quoteDetails?.total_per_visit
                  }
                </p>
              </div>
            </div>
            <div className="mt-4 bg-white rounded-lg p-4">
              <p className="text-sm text-gray-600">Properties</p>
              <p className="text-lg font-semibold text-gray-900">{quoteDetails?.total_properties_count}</p>
            </div>
          </div>

          {/* Properties and Items */}
          <div className="space-y-4">
            <h3 className="text-lg font-medium text-gray-900 flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Properties & Items
            </h3>
            
            {editProperties.map((property, propertyIndex) => (
              <div key={property.id} className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4 text-gray-500" />
                      <h4 className="font-medium text-gray-900">Property {propertyIndex + 1}</h4>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="text-sm text-gray-600">
                        {property.quote_items?.length || 0} items
                      </div>
                      {isEditing && (
                        <button
                          onClick={() => addItemToProperty(property.id)}
                          className="text-sm bg-green-100 text-green-700 px-3 py-1 rounded-full hover:bg-green-200 transition-colors flex items-center gap-1"
                        >
                          <Plus className="w-3 h-3" />
                          Add Item
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="text-sm text-gray-600 mt-1">{property.address}</p>
                  {property.latitude && property.longitude && (
                    <p className="text-xs text-gray-500 mt-1">
                      Coordinates: {property.latitude}, {property.longitude}
                    </p>
                  )}
                </div>
                
                <div className="p-6">
                  {property.quote_items?.length > 0 ? (
                    <div className="space-y-4">
                      {property.quote_items.map((quoteItem) => (
                        <div key={quoteItem.id} className="border border-gray-200 rounded-lg p-4">
                          {isEditing ? (
                            // Edit Mode
                            <div className="grid grid-cols-1 md:grid-cols-6 gap-4 items-center">
                              <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Item</label>
                                <select
                                  value={quoteItem.item_id}
                                  onChange={(e) => updatePropertyItem(property.id, quoteItem.id, 'item_id', e.target.value)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                                >
                                  <option value="">Select an item</option>
                                  {availableItems.map(availableItem => (
                                    <option key={availableItem.id} value={availableItem.id}>
                                      {availableItem.name}
                                    </option>
                                  ))}
                                </select>
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
                                <input
                                  type="number"
                                  min="1"
                                  value={quoteItem.quantity}
                                  onChange={(e) => updatePropertyItem(property.id, quoteItem.id, 'quantity', parseInt(e.target.value) || 0)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Unit Rental</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={quoteItem.unit_rental_price}
                                  onChange={(e) => updatePropertyItem(property.id, quoteItem.id, 'unit_rental_price', parseFloat(e.target.value) || 0)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Unit Laundering</label>
                                <input
                                  type="number"
                                  step="0.01"
                                  value={quoteItem.unit_laundering_price}
                                  onChange={(e) => updatePropertyItem(property.id, quoteItem.id, 'unit_laundering_price', parseFloat(e.target.value) || 0)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                                />
                              </div>
                              <div className="flex items-center gap-2">
                                <button
                                  onClick={() => removeItemFromProperty(property.id, quoteItem.id)}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Remove Item"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </div>
                          ) : (
                            // View Mode
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <Package className="w-4 h-4 text-gray-500" />
                                <div>
                                  <p className="font-medium text-gray-900">{quoteItem.item?.name}</p>
                                  <p className="text-sm text-gray-600">Quantity: {quoteItem.quantity}</p>
                                </div>
                              </div>
                              <div className="text-right">
                                <p className="font-medium text-gray-900">
                                  ${quoteItem.total_rental_price} rental
                                </p>
                                <p className="text-sm text-gray-600">
                                  ${quoteItem.total_laundering_price} laundering
                                </p>
                              </div>
                            </div>
                          )}
                          
                          {isEditing && (
                            <div className="mt-3 grid grid-cols-2 gap-4">
                              <div className="bg-gray-50 rounded-lg p-3">
                                <p className="text-sm text-gray-600">Total Rental</p>
                                <p className="font-medium text-gray-900">${(parseFloat(quoteItem.total_rental_price || 0)).toFixed(2)}</p>
                              </div>
                              <div className="bg-gray-50 rounded-lg p-3">
                                <p className="text-sm text-gray-600">Total Laundering</p>
                                <p className="font-medium text-gray-900">${(parseFloat(quoteItem.total_laundering_price || 0)).toFixed(2)}</p>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                      
                      <div className="bg-gray-50 rounded-lg p-4 mt-4">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-gray-900">Property Total:</span>
                          <span className="font-semibold text-gray-900">
                            ${(property.quote_items?.reduce((sum, item) => 
                              sum + parseFloat(item.total_rental_price || 0) + parseFloat(item.total_laundering_price || 0), 0
                            ) || 0).toFixed(2)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-4">No items for this property</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Status Update Modal */}
      {isStatusModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Update Quote Status</h3>
              <button
                onClick={() => setIsStatusModalOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status
                </label>
                <select
                  value={statusUpdate.status}
                  onChange={(e) => setStatusUpdate(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  <option value="pending">Pending</option>
                  <option value="reviewed">Reviewed</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Admin Notes
                </label>
                <textarea
                  value={statusUpdate.admin_notes}
                  onChange={(e) => setStatusUpdate(prev => ({ ...prev, admin_notes: e.target.value }))}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="Add notes about this quote..."
                />
              </div>
              
              <div className="flex gap-3 pt-4">
                <button
                  onClick={async () => {
                    await handleStatusUpdate();
                    setIsStatusModalOpen(false);
                  }}
                  className="btn-primary flex-1"
                >
                  Update Status
                </button>
                <button
                  onClick={() => setIsStatusModalOpen(false)}
                  className="btn-secondary flex-1"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuoteDetailsPage; 